import {
    activityCategories,
    ActivityCategory,
    ActivityRecordType,
    MarketingGoalTargets,
    OutcomeGoalMetric,
    outcomeGoalMetrics,
    outcomeGoalMetricShortLabels,
    TaskTypeId,
} from "@sp-crm/core";
import {
    ActivityTypeOption,
    activityTypesForRecordType,
} from "components/tasks/activity-type-options";
import { activityCategoryBarColor, outcomeGoalMetricBarColor } from "./pace";

export const marketingGoalRecordType: ActivityRecordType = "referenceContact";

export const goalActivityTypeOptions = (
    options: ActivityTypeOption[],
    referencedTaskTypeIds: Set<string>,
): ActivityTypeOption[] =>
    activityTypesForRecordType(
        options,
        marketingGoalRecordType,
        Array.from(referencedTaskTypeIds) as TaskTypeId[],
    );

export type GoalColumn =
    | {
          kind: "activity";
          taskTypeId: TaskTypeId;
          label: string;
          category: ActivityCategory;
      }
    | { kind: "outcome"; metric: OutcomeGoalMetric; label: string };

export const goalColumnKey = (column: GoalColumn): string =>
    column.kind === "activity" ? column.taskTypeId : column.metric;

const categoryOrder = (category: ActivityCategory): number =>
    activityCategories.indexOf(category);

export interface GoalColumnOrdering {
    category: ActivityCategory;
    label: string;
}

export const compareGoalColumnOrder = (
    a: GoalColumnOrdering,
    b: GoalColumnOrdering,
): number =>
    categoryOrder(a.category) - categoryOrder(b.category) ||
    a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase());

export const buildGoalColumns = (
    options: ActivityTypeOption[],
    referencedTaskTypeIds: Set<string>,
): GoalColumn[] => {
    const activityColumns: GoalColumn[] = goalActivityTypeOptions(
        options,
        referencedTaskTypeIds,
    )
        .filter(option => !option.isArchived || referencedTaskTypeIds.has(option.id))
        .map(option => ({
            kind: "activity" as const,
            taskTypeId: option.id,
            label: option.name,
            category: option.category,
        }))
        .sort(compareGoalColumnOrder);
    const outcomeColumns: GoalColumn[] = outcomeGoalMetrics.map(metric => ({
        kind: "outcome",
        metric,
        label: outcomeGoalMetricShortLabels[metric],
    }));
    return [...outcomeColumns, ...activityColumns];
};

export const goalColumnValue = (
    targets: MarketingGoalTargets,
    column: GoalColumn,
): number =>
    column.kind === "activity"
        ? (targets.activity[column.taskTypeId] ?? 0)
        : targets[column.metric];

export const goalColumnBarColor = (column: GoalColumn): string =>
    column.kind === "activity"
        ? activityCategoryBarColor[column.category]
        : outcomeGoalMetricBarColor[column.metric];

export const referencedActivityTypeIds = (
    targetSets: MarketingGoalTargets[],
): Set<string> => {
    const ids = new Set<string>();
    for (const targets of targetSets) {
        for (const taskTypeId of Object.keys(targets.activity)) {
            ids.add(taskTypeId);
        }
    }
    return ids;
};
